import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Icon } from "@/components/Icon";
import { Badge } from "@/components/Badge";
import { CompletePhaseModal } from "@/pages/Developments/CompletePhaseModal";
import { developmentApi } from "@/lib/api";
import {
    canUserSignWorkflowStep,
    canUserRevertWorkflowStep,
    getStepRoleLabelKey,
    getStepDurationLabel,
    jumpDesign,
    jumpTasks,
    type WorkflowStep,
} from "@/utils/stepRole";

export type Step = WorkflowStep;

interface DevelopmentTimelineProps {
    className?: string;
    development: {
        id: number;
        client_id?: number;
        created_at?: string;
        steps?: Step[];
        participants?: Array<{ user_id: number; role: "viewer" | "editor" }>;
        attachments?: Array<{
            id: number;
            name: string;
            category?: string;
            is_internal?: boolean;
        }>;
    };
    onUpdate: (data: Record<string, unknown>) => void;
    onSelectStep?: (step: Step) => void;
    onOpenDesignModal?: () => void;
}

export const DevelopmentTimeline: React.FC<DevelopmentTimelineProps> = ({
    className = "",
    development,
    onUpdate,
    onSelectStep,
    onOpenDesignModal,
}) => {
    const { t } = useTranslation("global");
    const { user, isIt, isClient, isDevelopment } = useAuth();
    const { showSuccess } = useToast();
    const [showStepSignModal, setShowStepSignModal] = useState<boolean>(false);
    const [pendingStepKey, setPendingStepKey] = useState<string | null>(null);
    const [pendingStepReopen, setPendingStepReopen] = useState<boolean>(false);
    const [isSigningStep, setIsSigningStep] = useState<boolean>(false);
    const [isGeneratingDoc, setIsGeneratingDoc] = useState<boolean>(false);
    const [stepSignError, setStepSignError] = useState<string | null>(null);

    const currentParticipant = development.participants?.find(
        (p) => p.user_id === user?.id
    );
    const isEditor = currentParticipant
        ? currentParticipant.role === "editor"
        : isIt || isDevelopment || development.client_id === user?.id;

    const steps = development?.steps || [];
    const pendingStep = pendingStepKey
        ? (steps.find(
              (step) => (step.tag || step.step_key || "") === pendingStepKey
          ) ?? null)
        : null;

    const handleStepClick = (step: any) => {
        const isDone = step.is_completed;
        const steps = development.steps || [];
        let idx = steps.findIndex(
            (s) => (s.step_key || s.tag) === (step.step_key || step.tag)
        );
        let searchStep = step;

        if (idx === -1 && (step as any)._group_first_step) {
            searchStep = (step as any)._group_first_step;
            idx = steps.findIndex(
                (s) =>
                    (s.step_key || s.tag) ===
                    (searchStep.step_key || searchStep.tag)
            );
        }

        const isCurrent =
            !isDone && (idx === 0 || Boolean(steps[idx - 1]?.is_completed));

        if (isDone || isCurrent) {
            onSelectStep?.({
                step_key: searchStep.step_key || searchStep.tag,
                title: step.title,
            });
        }
    };

    const openStepSignModal = (stepKey: string, reopen = false): void => {
        setPendingStepKey(stepKey);
        setPendingStepReopen(reopen);
        setStepSignError(null);
        setIsSigningStep(false);
        setShowStepSignModal(true);
    };

    const requirementDocsCount = useMemo(
        () =>
            (development.attachments ?? []).filter(
                (a) =>
                    a.category === "requirements_document" &&
                    (!a.is_internal || a.name.toLowerCase().endsWith(".pdf"))
            ).length,
        [development.attachments]
    );

    const designDocs = useMemo(
        () =>
            (development.attachments ?? []).filter(
                (a) =>
                    a.category === "design" ||
                    a.name.toLowerCase().includes("diseno") ||
                    a.name.toLowerCase().includes("diseño")
            ),
        [development.attachments]
    );

    const designDocsCount = designDocs.length;

    const displaySteps = useMemo(() => {
        if (!isClient) return steps;

        const groupTags = ["tasks", "frontend", "backend", "tests"];
        const filtered: Step[] = [];
        let groupAdded = false;

        steps.forEach((step) => {
            const tag = step.tag || step.step_key || "";
            if (groupTags.includes(tag)) {
                if (!groupAdded) {
                    const allGroupSteps = steps.filter((s) =>
                        groupTags.includes(s.tag || s.step_key || "")
                    );
                    if (allGroupSteps.length > 0) {
                        const isAllDone = allGroupSteps.every(
                            (s) => s.is_completed
                        );
                        const activeOrLast =
                            allGroupSteps.find((s) => !s.is_completed) ||
                            allGroupSteps[allGroupSteps.length - 1];
                        const firstStep = allGroupSteps[0];

                        filtered.push({
                            ...activeOrLast,
                            id: 99999,
                            tag: "development",
                            step_key: "development",
                            title: "Desarrollo",
                            is_completed: isAllDone,
                            // Store the original start point to calculate duration properly
                            _group_first_step: firstStep,
                        } as any);
                    }
                    groupAdded = true;
                }
            } else {
                filtered.push(step);
            }
        });
        return filtered;
    }, [steps, isClient]);

    const activeStepIndex = displaySteps.findIndex((step) => !step.is_completed);
    const designStepIndex = displaySteps.findIndex(
        (step) => step.tag === "design"
    );
    const designIsPastActiveFlow =
        designStepIndex >= 0 &&
        activeStepIndex > designStepIndex;

    const handleGenerateRequirements = async (): Promise<void> => {
        setIsGeneratingDoc(true);
        setStepSignError(null);

        try {
            const genRes = await developmentApi.generateRequirements(
                development.id
            );
            if (!genRes.result) {
                throw new Error(
                    genRes.message ||
                        t("developments.detail.requirements_documents_error")
                );
            }

            const detail = await developmentApi.get(development.id);
            if (detail.result) {
                onUpdate(detail.data);
            }
            showSuccess(
                t("developments.detail.requirements_documents_success")
            );
        } catch (err) {
            setStepSignError(
                err instanceof Error
                    ? err.message
                    : t("developments.detail.requirements_documents_error")
            );
        } finally {
            setIsGeneratingDoc(false);
        }
    };

    const handleConfirmSignStep = async (
        shouldGenerateDoc: boolean = false
    ): Promise<void> => {
        if (!pendingStepKey) {
            return;
        }

        setIsSigningStep(true);
        setStepSignError(null);

        try {
            if (!pendingStepReopen && shouldGenerateDoc) {
                const genRes = await developmentApi.generateRequirements(
                    development.id
                );
                if (!genRes.result) {
                    throw new Error(
                        genRes.message ||
                            t(
                                "developments.detail.requirements_documents_error"
                            )
                    );
                }
            }

            const res = await developmentApi.signStep(
                development.id,
                pendingStepKey,
                undefined,
                !pendingStepReopen
            );

            if (res.result) {
                setShowStepSignModal(false);
                setPendingStepKey(null);
                setPendingStepReopen(false);
                onUpdate(res.data);
            } else {
                setStepSignError(
                    res.message || t("developments.detail.step_sign_error")
                );
            }
        } catch (err) {
            setStepSignError(
                err instanceof Error
                    ? err.message
                    : t("developments.detail.step_sign_error")
            );
        } finally {
            setIsSigningStep(false);
        }
    };

    return (
        <div
            className={`${className} bg-box rounded-2xl border border-slate-200/80 p-5 shadow-xs space-y-4`}
        >
            <div className="flex items-center justify-center border-b border-slate-100 dark:border-zinc-800 pb-3">
                <div className="flex items-center space-x-2 text-center">
                    <Icon value="clock" size={17} className="text-primary" />
                    <h3 className="text-sm font-bold text-slate-900 dark:text-zinc-100 uppercase tracking-wider">
                        {t("developments.detail.section_phases_title_short", {
                            completed: displaySteps.filter(
                                (s) => s.is_completed
                            ).length,
                            total: displaySteps.length,
                        })}
                    </h3>
                </div>
            </div>

            {/* Stepper Horizontal Scrollable Bar with top padding to avoid clipping rings/shadows */}
            <div className="overflow-x-auto px-1">
                <div className="flex items-start min-w-[750px] space-x-2">
                    {displaySteps.map((step, idx) => {
                        const isDone = step.is_completed;
                        const isCurrent =
                            !isDone &&
                            (idx === 0 ||
                                Boolean(displaySteps[idx - 1]?.is_completed));
                        const stepTag = step.tag || "";
                        const stepIdentifier = stepTag || step.step_key || "";
                        const isSkipped =
                            ((jumpDesign || designIsPastActiveFlow) &&
                                stepTag === "design") ||
                            (jumpTasks && stepTag === "tasks");

                        // For duration, if it's the grouped step, calculate manually
                        let durationLabel: string | null = null;
                        if ((step as any)._group_first_step) {
                            if (isDone) {
                                const start = new Date(
                                    development.created_at || ""
                                ); // Fallback, could be refined
                                const firstKey =
                                    (step as any)._group_first_step.tag ||
                                    (step as any)._group_first_step.step_key;
                                const originalIdx = steps.findIndex(
                                    (s) => (s.tag || s.step_key) === firstKey
                                );
                                if (
                                    originalIdx > 0 &&
                                    steps[originalIdx - 1]?.signed_at
                                ) {
                                    start.setTime(
                                        new Date(
                                            steps[originalIdx - 1]
                                                .signed_at as string
                                        ).getTime()
                                    );
                                } else if (steps[originalIdx]?.created_at) {
                                    start.setTime(
                                        new Date(
                                            steps[originalIdx]
                                                .created_at as string
                                        ).getTime()
                                    );
                                }

                                const end = step.signed_at
                                    ? new Date(step.signed_at)
                                    : new Date();
                                const diffMs = Math.max(
                                    end.getTime() - start.getTime(),
                                    0
                                );
                                const totalSeconds = Math.floor(diffMs / 1000);
                                const d = Math.floor(totalSeconds / 86400);
                                const h = Math.floor(
                                    (totalSeconds % 86400) / 3600
                                );
                                const m = Math.floor(
                                    (totalSeconds % 3600) / 60
                                );
                                durationLabel =
                                    `${d > 0 ? d + "d" : ""}${h > 0 ? h + "h" : ""}${m > 0 ? m + "m" : ""}` ||
                                    "0s";
                            }
                        } else {
                            durationLabel = getStepDurationLabel(
                                steps,
                                step,
                                development.created_at,
                                t
                            );
                        }

                        const canSign = canUserSignWorkflowStep(
                            stepIdentifier,
                            {
                                isCurrent,
                                isEditor,
                                isClient,
                                isIt,
                            }
                        );
                        const canReopen =
                            isDone &&
                            canUserRevertWorkflowStep(stepIdentifier, {
                                isCurrent,
                                isEditor,
                                isClient,
                                isIt,
                            });

                        return (
                            <div
                                key={
                                    step.id
                                        ? `step-${step.id}`
                                        : stepIdentifier
                                          ? `step-${stepIdentifier}-${idx}`
                                          : `step-idx-${idx}`
                                }
                                className="w-full"
                            >
                                <div
                                    onClick={() => {
                                        if (!isSkipped) {
                                            handleStepClick(step);
                                        }
                                    }}
                                    className={`flex-1 flex flex-col items-center text-center relative group p-1.5 rounded-xl transition-all ${
                                        !isSkipped && (isDone || isCurrent)
                                            ? "cursor-pointer hover:bg-slate-50/60 dark:hover:bg-zinc-800/40"
                                            : "cursor-default"
                                    }`}
                                    title={
                                        !isSkipped && (isDone || isCurrent)
                                            ? t(
                                                  "developments.detail.view_step_in_chat"
                                              )
                                            : undefined
                                    }
                                >
                                    {/* Step Icon Circle */}
                                    <div className="relative">
                                        <div
                                            className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs transition-all shadow-xs shrink-0 ${
                                                isSkipped
                                                    ? "bg-slate-100 text-slate-400 border border-slate-200 dark:bg-zinc-800 dark:text-zinc-500 dark:border-zinc-700"
                                                    : isDone
                                                    ? "bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800 group-hover:ring-2 group-hover:ring-emerald-400 group-hover:border-emerald-400 group-hover:shadow-sm"
                                                    : isCurrent
                                                      ? "bg-primary text-white ring-4 ring-primary/20"
                                                      : "bg-slate-100 text-slate-400 border border-slate-200 dark:bg-zinc-800 dark:text-zinc-400 dark:border-zinc-700"
                                            }`}
                                        >
                                            {idx + 1}
                                        </div>
                                        {isDone && !isSkipped && (
                                            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-xs">
                                                <Icon
                                                    value="check"
                                                    size={10}
                                                    strokeWidth={3}
                                                />
                                            </span>
                                        )}
                                    </div>

                                    {/* Step Role Required - Badge in gray with dark text for both CLIENTE and IT */}
                                    <div className="mt-2.5 h-5 flex items-center justify-center">
                                        <Badge
                                            variant="transparent"
                                            size="sm"
                                            className="uppercase font-bold tracking-wider text-slate-900 dark:text-zinc-100 dark:bg-zinc-800 dark:border-zinc-700"
                                        >
                                            {t(
                                                getStepRoleLabelKey(
                                                    stepIdentifier
                                                )
                                            )}
                                        </Badge>
                                    </div>

                                    {/* Step Title - Positioned below the badge, aligned to top so all titles match line height */}
                                    <div className="mt-2 flex flex-col items-center justify-start text-center px-1">
                                        <span
                                            className={`text-xs leading-snug line-clamp-2 transition-colors ${
                                                isSkipped
                                                    ? "text-slate-400/60 dark:text-zinc-500"
                                                    : isDone
                                                    ? canReopen
                                                        ? "text-slate-800 dark:text-zinc-200 group-hover:text-primary dark:group-hover:text-primary-400"
                                                        : "text-slate-800 dark:text-zinc-200 group-hover:text-primary dark:group-hover:text-primary-400"
                                                    : isCurrent
                                                      ? "font-bold text-primary dark:text-primary-400"
                                                      : "text-slate-400/60 dark:text-zinc-500"
                                            }`}
                                        >
                                            {stepIdentifier === "development"
                                                ? step.title
                                                : step.tag
                                                  ? t(
                                                        "developments.detail.timeline.steps." +
                                                            step.tag
                                                    )
                                                  : step.title}
                                        </span>
                                    </div>

                                    {/* Hover Mini-Backdrop to complete the step when actionable or to go back to that step */}
                                    {!isSkipped && (canSign || canReopen) && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (canSign) {
                                                    openStepSignModal(
                                                        stepIdentifier,
                                                        false
                                                    );
                                                } else {
                                                    openStepSignModal(
                                                        stepIdentifier,
                                                        true
                                                    );
                                                }
                                            }}
                                            className="absolute inset-0 bg-slate-900/55 dark:bg-zinc-950/65 backdrop-blur-[2px] rounded-xl flex flex-col items-center justify-center gap-1.5 text-white opacity-0 group-hover:opacity-100 cursor-pointer transition-all duration-200 z-20 p-2 shadow-md"
                                            title={
                                                canSign
                                                    ? t(
                                                          "developments.detail.step_complete_btn"
                                                      )
                                                    : t(
                                                          "developments.detail.step_reopen_btn"
                                                      )
                                            }
                                        >
                                            <Icon
                                                value={
                                                    canSign ? "check" : "undo"
                                                }
                                                size={32}
                                                strokeWidth={3}
                                                className={
                                                    canSign
                                                        ? "text-green-400"
                                                        : "text-red-300"
                                                }
                                            />
                                            <span
                                                className={`${canSign ? "text-green-400" : "text-red-300"} text-[11px] font-bold tracking-wide uppercase drop-shadow-xs`}
                                            >
                                                {t(
                                                    canSign
                                                        ? "developments.detail.step_complete_btn"
                                                        : "developments.detail.step_reopen_btn"
                                                )}
                                            </span>
                                        </button>
                                    )}
                                </div>
                                <div className="flex justify-center -mt-1 min-h-[15px]">
                                    {durationLabel && !isSkipped && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-800 dark:text-zinc-200 group-hover:text-primary dark:group-hover:text-primary-400">
                                            <Icon value="clock" size={10} />
                                            {durationLabel}
                                        </span>
                                    )}
                                </div>
                                {!isSkipped && (isDone || isCurrent) && (
                                    <div
                                        className="cursor-pointer hover:scale-[1.1] active:scale-[0.98] transition-transform duration-200 ease-out inline-flex w-full 
                                        justify-center items-center gap-1 text-[10px] font-medium whitespace-nowrap
                                        text-primary/90 dark:text-primary-400
                                        "
                                        onClick={() => handleStepClick(step)}
                                    >
                                        <Icon value="message" size={10} />
                                        <span>
                                            {t(
                                                "developments.detail.view_step_in_chat"
                                            )}
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <CompletePhaseModal
                isOpen={showStepSignModal}
                onClose={() => {
                    setShowStepSignModal(false);
                    setPendingStepKey(null);
                    setPendingStepReopen(false);
                    setStepSignError(null);
                    setIsSigningStep(false);
                }}
                step={pendingStep}
                steps={steps}
                developmentCreatedAt={development.created_at}
                onConfirm={(shouldGenerateDoc) => {
                    void handleConfirmSignStep(shouldGenerateDoc);
                }}
                isSubmitting={isSigningStep}
                error={stepSignError}
                requirementDocsCount={requirementDocsCount}
                onGenerateDocument={handleGenerateRequirements}
                isGeneratingDocument={isGeneratingDoc}
                designDocs={designDocs as any[]}
                designDocsCount={designDocsCount}
                onOpenDesignModal={onOpenDesignModal}
                isReopen={pendingStepReopen}
            />
        </div>
    );
};
